import Image from 'next/image'
import Link from 'next/link'

import type { JSX } from 'react'

import type { BlogPost } from '@types'

import { Chip } from '../../design-system'
import { formatPostDate, postUrl } from './post-date'

type BlogCardProps = { post: BlogPost; variant?: 'grid' | 'featured' }

export default function BlogCard({ post, variant = 'grid' }: BlogCardProps): JSX.Element {
  const featured = variant === 'featured'
  const category = post.categories?.[0]?.name
  // Sizes may be missing at runtime although the type says otherwise; no usable url means no image. [S20-1320]
  const image = post.image?.sizes?.MEDIUM_LARGE ?? post.image?.sizes?.FULL
  const Heading = featured ? 'h2' : 'h3'

  return (
    <Link
      href={postUrl(post)}
      data-pw="post-card"
      className={`flex overflow-hidden rounded-lg border border-border-strong bg-surface text-ink no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
        featured ? 'flex-row max-[761px]:flex-col' : 'flex-col'
      }`}
    >
      <div className={`relative aspect-[16/9] w-full ${featured ? 'min-[762px]:w-1/2 min-[762px]:flex-none' : ''}`}>
        {image?.url ? (
          <Image
            src={image.url}
            alt={post.image?.alt || post.title}
            fill
            // The featured card is the LCP of /blogs; grid cards stay lazy. [S20-1320]
            preload={featured}
            sizes={
              featured ? '(max-width: 761px) 100vw, 50vw' : '(max-width: 761px) 100vw, (max-width: 1024px) 50vw, 33vw'
            }
            className="object-cover"
          />
        ) : (
          <div
            aria-hidden="true"
            className="flex size-full items-center justify-center bg-surface-chrome font-sans text-xl font-medium text-ink-muted"
          >
            SonicJobs
          </div>
        )}
      </div>
      <div className="flex flex-col gap-[12px] p-[20px]">
        <div className="flex flex-wrap items-center gap-[8px] font-sans text-xs text-ink-muted">
          {featured && <Chip variant="accent">Featured</Chip>}
          {category && <span>{category}</span>}
          {category && <span aria-hidden="true">·</span>}
          <span>{formatPostDate(post.date)}</span>
        </div>
        <Heading
          className={`m-0 font-sans font-medium tracking-lead text-ink ${featured ? 'text-h3' : 'text-xl leading-tight'}`}
        >
          {post.title}
        </Heading>
        <p className="m-0 line-clamp-3 font-sans text-base text-ink-secondary">{post.excerpt || post.subTitle}</p>
      </div>
    </Link>
  )
}
